import type {
  GroupAnalysisCertainty,
  GroupAnalysisComplianceType,
  GroupAnalysisDealSignalType,
  GroupAnalysisLabels,
  GroupAnalysisTier,
  GroupAnalysisTodayReason,
  GroupAnalysisView,
  GroupAnalysisViewCounts,
} from '../../api/types.js';

/**
 * stage/risk/fact 的中文标签来自 API 返回的 Profile labels（商家语义在后端），
 * 这里只保留与商家无关的通用映射。
 */

/** 商家 Profile 展示名；未知 key 原样展示。 */
export const PROFILE_LABELS: Record<string, string> = {
  qitu: '七兔',
  sanchuan: '三川',
};

export const CERTAINTY_LABELS: Record<GroupAnalysisCertainty, string> = {
  explicit: '明确',
  inferred: '推断',
  unknown: '未知',
};

export const RUN_ERROR_LABELS: Record<string, string> = {
  evidence_rejected: '证据校验未通过',
  schema_invalid: '模型输出结构无效',
  input_too_large: '输入超过上限',
  model_failed: '模型调用失败',
  model_timeout: '模型调用超时',
  stale_reclaimed: '运行超时回收',
};

export const NEXT_STEP_OWNER_LABELS: Record<'customer' | 'service', string> = {
  customer: '客户',
  service: '我方',
};

export const TIER_LABELS: Record<GroupAnalysisTier, string> = {
  A: 'A 档',
  B: 'B 档',
  C: 'C 档',
  D: 'D 档',
};

export const LIFECYCLE_STAGE_LABELS: Record<string, string> = {
  repeat: '复购客户',
  customer: '已成交客户',
  intent: '意向客户',
  new: '新接触',
};

export const DEAL_SIGNAL_LABELS: Record<GroupAnalysisDealSignalType, string> = {
  deposit: '定金',
  balance: '尾款',
  paid: '全款',
  order_no: '单号',
  shipped: '已发货',
  store_visit_booked: '约到店',
};

export const COMPLIANCE_LABELS: Record<GroupAnalysisComplianceType, string> = {
  abuse: '辱骂客户',
  private_deal: '私下收款',
  poaching: '撬客户',
  overpromise: '超权限承诺',
  price_leak: '泄底价',
};

export const STAGE_SOURCE_LABELS: Record<'event' | 'model', string> = {
  event: '事件',
  model: '模型',
};

export function tierLabel(tier: GroupAnalysisTier | null | undefined): string {
  return tier ? TIER_LABELS[tier] : '未评级';
}

export function lifecycleStageLabel(value: string | null | undefined): string {
  return value ? LIFECYCLE_STAGE_LABELS[value] ?? value : '-';
}

export function dealSignalLabel(type: string): string {
  return DEAL_SIGNAL_LABELS[type as GroupAnalysisDealSignalType] ?? type;
}

export function complianceLabel(type: string): string {
  return COMPLIANCE_LABELS[type as GroupAnalysisComplianceType] ?? type;
}

export function stageSourceLabel(source: 'event' | 'model' | null | undefined): string | null {
  return source ? STAGE_SOURCE_LABELS[source] : null;
}

/** ¥{min}–¥{max}，min===max 时只显示一个值；两端都是千分位。 */
export function pipelineValueLabel(value: { min: number; max: number } | null | undefined): string {
  if (!value) return '-';
  const format = (amount: number) => `¥${amount.toLocaleString('zh-CN')}`;
  return value.min === value.max ? format(value.min) : `${format(value.min)}–${format(value.max)}`;
}

/** 需求完整度 filled/total；无 computed 或 total=0（Profile 无必填事实）时显示 '-'。 */
export function completenessLabel(value: { filled: number; total: number } | null | undefined): string {
  return value && value.total > 0 ? `${value.filled}/${value.total}` : '-';
}

export function profileLabel(key: string): string {
  return PROFILE_LABELS[key] ?? key;
}

export function stageLabel(labels: GroupAnalysisLabels | null | undefined, value: string): string {
  return labels?.stages[value] ?? value;
}

export function riskLabel(labels: GroupAnalysisLabels | null | undefined, type: string): string {
  return labels?.risks[type] ?? type;
}

export function factLabel(labels: GroupAnalysisLabels | null | undefined, key: string): string {
  return labels?.facts[key] ?? key;
}

export function intentLabel(level: string): string {
  return level === 'high' ? '高' : level === 'medium' ? '中' : level === 'low' ? '低' : '未知';
}

export function severityLabel(severity: string): string {
  return severity === 'high' ? '高' : severity === 'medium' ? '中' : '低';
}

export function runErrorLabel(code: string | null): string {
  return code ? RUN_ERROR_LABELS[code] ?? code : '未知原因';
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(date);
}

/** 名单页签：三组带计数，每个页签一行固定说明（老板拿它干嘛 · 销售拿它干嘛）。 */
export interface AnalysisViewTab {
  view: GroupAnalysisView;
  label: string;
  countKey: keyof GroupAnalysisViewCounts;
  boss: string;
  sales: string;
}

export const ANALYSIS_VIEW_GROUPS: ReadonlyArray<{ title: string; tabs: readonly AnalysisViewTab[] }> = [
  {
    title: '名单',
    tabs: [
      { view: 'all', label: '全部', countKey: 'all', boss: '看全盘档位分布与谁在推进', sales: '找自己负责的群' },
      { view: 'today', label: '今天该跟的', countKey: 'today', boss: '看今天谁该被跟、有没有人接', sales: '今天先做这几件' },
      { view: 'new', label: '新群待破冰', countKey: 'new', boss: '看新客进来有没有人接', sales: '破冰第一句、补齐需求' },
      { view: 'deal_pending', label: '成交信号待核', countKey: 'dealPending', boss: '核实成交是否真实到账', sales: '补单号、催尾款' },
    ],
  },
  {
    title: '在丢的',
    tabs: [
      { view: 'missed', label: '漏跟', countKey: 'missed', boss: '看谁把客户晾着了（含历史）', sales: '立刻回，别让客户等' },
      { view: 'lost', label: '高价值断联', countKey: 'lost', boss: 'A 档客户断联了，人盯着救', sales: '值钱的客户，亲自找由头再触达' },
      { view: 'dormant', label: '沉睡 ≥30 天', countKey: 'dormant', boss: '看沉睡面有多大', sales: '活动/内容触达，别硬推' },
      { view: 'churn', label: '流失明细', countKey: 'churn', boss: '看流失与预计损失', sales: '复盘丢单原因' },
      { view: 'pipeline', label: '在途', countKey: 'pipeline', boss: '看在途金额压在哪几群', sales: '盯 A/B 档往下推' },
    ],
  },
  {
    title: '出事的',
    tabs: [
      { view: 'risk', label: '风险单', countKey: 'risk', boss: '核实风险单：确认或误报', sales: '自查话术，别越权承诺' },
    ],
  },
];

export function analysisViewTab(view: GroupAnalysisView): AnalysisViewTab {
  for (const group of ANALYSIS_VIEW_GROUPS) {
    const tab = group.tabs.find((item) => item.view === view);
    if (tab) return tab;
  }
  return ANALYSIS_VIEW_GROUPS[0]!.tabs[0]!;
}

export function isAnalysisView(value: string | null | undefined): value is GroupAnalysisView {
  return ANALYSIS_VIEW_GROUPS.some((group) => group.tabs.some((tab) => tab.view === value));
}

/** ¥ 金额；null（sales 脱敏）显示 —。 */
export function moneyLabel(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `¥${Math.round(value).toLocaleString('zh-CN')}`;
}

/** ¥min–¥max；任一端为 null（sales 脱敏）显示 —；相等只显示一个。 */
export function moneyRangeLabel(min: number | null | undefined, max: number | null | undefined): string {
  if (min === null || min === undefined || max === null || max === undefined) return '—';
  return min === max ? moneyLabel(min) : `${moneyLabel(min)}–${moneyLabel(max)}`;
}

/** 秒数口语化：<60 秒按秒，<60 分钟按分钟，否则按小时（一位小数）。 */
export function secondsLabel(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${Math.round(seconds)} 秒`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} 分钟`;
  return `${(seconds / 3600).toFixed(1)} 小时`;
}

export function shareLabel(share: number | null | undefined): string {
  return share === null || share === undefined ? '—' : `${(share * 100).toFixed(1)}%`;
}

/** 跟进过程：最后一句是谁说的。 */
export function lastSpeakerLabel(speaker: 'customer' | 'service' | null | undefined): string {
  return speaker === 'customer' ? '客户在等' : speaker === 'service' ? '我方已回' : '—';
}

export const TODAY_REASON_LABELS: Record<GroupAnalysisTodayReason, string> = {
  next_step_overdue: '承诺到期',
  missed: '客户在等，没人回',
  deal_pending: '成交待核',
  new_customer_message: '有新消息',
};

export function todayReasonLabel(reason: GroupAnalysisTodayReason): string {
  return TODAY_REASON_LABELS[reason] ?? reason;
}

/** 档位中文名与经营含义（原型 v16：重点 / 意向 / 培育 / 沉默）。 */
export const TIER_NAMES: Record<GroupAnalysisTier | 'none', string> = {
  A: '重点客户',
  B: '意向客户',
  C: '培育客户',
  D: '沉默客户',
  none: '未评级',
};

export const TIER_DESC: Record<GroupAnalysisTier | 'none', string> = {
  A: '人盯，每周必触达，老板叫得出名字',
  B: '今日队列常客，补临门一脚',
  C: 'AI 养着，活动/内容触达',
  D: 'AI 全托管，只进活动池',
  none: '还没分析或旧快照无档位',
};

export const INFERRED_LEVEL_LABELS: Record<'high' | 'medium' | 'low', string> = { high: '高', medium: '中', low: '低' };
export const DECISION_CYCLE_LABELS: Record<'fast' | 'slow' | 'nurture', string> = { fast: '快单', slow: '慢单', nurture: '长期培育' };
export const RADAR_AXES: ReadonlyArray<{ key: 'riskConcern' | 'priceSensitivity' | 'decisionCaution' | 'emotional' | 'trust'; label: string }> = [
  { key: 'riskConcern', label: '风险顾虑' },
  { key: 'priceSensitivity', label: '价格敏感' },
  { key: 'decisionCaution', label: '决策谨慎' },
  { key: 'emotional', label: '情绪化' },
  { key: 'trust', label: '信任程度' },
];

/** 意向动向：↑ / ↓ / — 与差值。 */
export function intentDeltaLabel(delta: number | null | undefined): string {
  if (delta === null || delta === undefined) return '';
  if (delta > 0) return `↑${delta}`;
  if (delta < 0) return `↓${Math.abs(delta)}`;
  return '→';
}
